import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Circle, Rocket, X } from "lucide-react";
import { api } from "@/lib/api";

export const Onboarding = ({ onGo }) => {
  const [status, setStatus] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    api.get("/onboarding").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  if (!status || hidden || status.dismissed) return null;
  const steps = [
    { key: "snippet_installed", n: 1, title: "Install your snippet", desc: "Add one line of JS to your landing pages", cta: "Get snippet", go: () => onGo("tracking") },
    { key: "account_connected", n: 2, title: "Connect an ad account", desc: "Link Google, Meta or Bing", cta: "Connect", go: () => onGo("campaigns") },
    { key: "plan_active", n: 3, title: "Choose your plan", desc: "Unlock full protection", cta: "See plans", go: () => { window.location.href = "/#pricing"; } },
  ];
  const doneCount = steps.filter((s) => status[s.key]).length;
  if (doneCount === steps.length) return null;

  const dismiss = () => {
    setHidden(true);
    api.patch("/onboarding").catch(() => {});
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="onboarding-card"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, height: 0 }}
        className="mb-6 rounded-xl bg-[#0B101A] border border-cyan-neon/20 p-5 relative overflow-hidden"
      >
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-cyan-neon/10 blur-[60px] pointer-events-none" />
        <button
          data-testid="onboarding-dismiss"
          onClick={dismiss}
          className="absolute top-3 right-3 p-1.5 rounded-md text-slate-500 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-4">
          <Rocket className="w-4 h-4 text-cyan-neon" />
          <h3 className="font-display text-lg text-white">Get fully protected</h3>
          <span className="font-mono text-[10px] text-slate-500 ml-1">{doneCount}/3 done</span>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          {steps.map((s) => {
            const done = status[s.key];
            return (
              <div
                key={s.key}
                data-testid={`onboarding-step-${s.n}`}
                className={`rounded-lg border p-4 ${done ? "bg-[#39FF14]/5 border-[#39FF14]/20" : "bg-[#040914] border-white/8"}`}
              >
                <div className="flex items-center gap-2">
                  {done ? <CheckCircle2 className="w-4 h-4 text-[#39FF14]" /> : <Circle className="w-4 h-4 text-slate-600" />}
                  <span className={`text-sm ${done ? "text-[#39FF14]" : "text-white"}`}>{s.title}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1.5">{s.desc}</p>
                {!done && (
                  <button
                    data-testid={`onboarding-go-${s.n}`}
                    onClick={s.go}
                    className="mt-3 px-3.5 py-1.5 rounded-md bg-cyan-neon text-[#040914] text-xs font-semibold hover:brightness-110 transition-[filter]"
                  >
                    {s.cta} →
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
